/**
 * Signed-in identity. `GET /api/me` is the auth gate's probe: a 401 means no
 * session (render the auth pages), success carries the profile the chrome
 * reads (user menu, greeting, chat viewer, account page).
 */

import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHubApi } from "./context";

export function useMeQuery() {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.me(),
    queryFn: () => api.me.get(),
    // A 401 is the signed-out signal, not a transient fault — never retried
    // (the shared retry policy already stops on 4xx).
    staleTime: 60_000,
  });
}

export function useUpdateMyDisplayNameMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.me.updateDisplayName(displayName),
    onSuccess: () => {
      // Exact: the caller's API-key list hangs off `["hub", "me", …]` and a
      // rename tells it nothing.
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.me(), exact: true });
    },
  });
}
